import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import {
  TABLE_STYLE_OPTIONS,
  FELT_OPTIONS,
  RAIL_OPTIONS,
  DECK_STYLE_OPTIONS,
  DECK_COLORWAYS,
  CARD_BACK_OPTIONS,
  CARD_BACK_PATTERNS,
  CHIP_COLOR_OPTIONS,
  CHIP_STYLE_OPTIONS,
  BACKGROUND_OPTIONS,
  GLOW_OPTIONS,
  DEFAULT_APPEARANCE,
  loadAppearance,
  saveAppearance,
  SOLVER_PALETTE,
  SOLVER_DECK_OPTIONS,
  DEFAULT_SOLVER_PREFS,
  loadSolverPrefs,
  saveSolverPrefs,
} from "../theme/appearancePrefs";

const C = {
  border: "#333333",
  text: "#e0e0e0",
  textMuted: "#8f8f8f",
  white: "#ffffff",
  green: "#56ECCF",
};
const SUIT_SYMBOLS = { s: "♠", h: "♥", d: "♦", c: "♣" };

function OptionChip({ active, label, onClick }) {
  return (
    <Button
      size="small"
      onClick={onClick}
      sx={{
        minWidth: "unset",
        px: 0.9,
        py: 0.25,
        borderRadius: "6px",
        border: `1px solid ${active ? C.green : C.border}`,
        backgroundColor: active ? "rgba(86,236,207,0.14)" : "rgba(255,255,255,0.02)",
        color: active ? C.green : C.textMuted,
        fontSize: "10.5px",
        fontWeight: active ? 800 : 600,
        textTransform: "none",
        lineHeight: 1.2,
        "&:hover": { borderColor: C.green, color: C.green, backgroundColor: "rgba(86,236,207,0.08)" },
      }}
    >
      {label}
    </Button>
  );
}

function Swatch({ selected, onClick, background, label }) {
  return (
    <Box
      onClick={onClick}
      title={label}
      sx={{
        width: 28,
        height: 28,
        borderRadius: "8px",
        background,
        border: `2px solid ${selected ? C.green : "rgba(255,255,255,0.18)"}`,
        boxShadow: selected ? "0 0 0 2px rgba(86,236,207,0.3)" : "none",
        cursor: "pointer",
        transition: "all 140ms ease",
        "&:hover": { borderColor: C.green },
      }}
    />
  );
}

function Row({ title, children }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.text, mb: 0.5 }}>{title}</Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, alignItems: "center" }}>{children}</Box>
    </Box>
  );
}

function TrainerSection({ appearance, onChange }) {
  return (
    <>
      <Row title="Table shape">
        {Object.entries(TABLE_STYLE_OPTIONS).map(([id, opt]) => (
          <OptionChip key={`shape-${id}`} label={opt.label} active={appearance.tableStyle === id} onClick={() => onChange({ tableStyle: id })} />
        ))}
      </Row>
      <Row title="Felt colour">
        {Object.entries(FELT_OPTIONS).map(([id, opt]) => (
          <Swatch
            key={`felt-${id}`}
            label={opt.label}
            selected={appearance.felt === id}
            onClick={() => onChange({ felt: id })}
            background={`radial-gradient(circle at 50% 35%, ${opt.c1} 0%, ${opt.c3} 100%)`}
          />
        ))}
      </Row>
      <Row title="Rail colour">
        {Object.entries(RAIL_OPTIONS).map(([id, opt]) => (
          <Swatch key={`rail-${id}`} label={opt.label} selected={appearance.rail === id} onClick={() => onChange({ rail: id })} background={opt.color} />
        ))}
      </Row>
      <Row title="Deck style">
        {Object.entries(DECK_STYLE_OPTIONS).map(([id, opt]) => (
          <OptionChip key={`deck-${id}`} label={opt.label} active={appearance.deckStyle === id} onClick={() => onChange({ deckStyle: id })} />
        ))}
      </Row>
      <Row title="Deck colours">
        {Object.entries(DECK_COLORWAYS).map(([id, opt]) => (
          <OptionChip key={`deckcol-${id}`} label={opt.label} active={appearance.deckColors === id} onClick={() => onChange({ deckColors: id })} />
        ))}
      </Row>
      <Row title="Card back colour">
        {Object.entries(CARD_BACK_OPTIONS).map(([id, opt]) => (
          <Swatch
            key={`back-${id}`}
            label={opt.label}
            selected={appearance.cardBack === id}
            onClick={() => onChange({ cardBack: id })}
            background={`linear-gradient(135deg, ${opt.accent}, #15161a)`}
          />
        ))}
      </Row>
      <Row title="Card back pattern">
        {Object.entries(CARD_BACK_PATTERNS).map(([id, opt]) => (
          <OptionChip key={`pattern-${id}`} label={opt.label} active={appearance.cardBackPattern === id} onClick={() => onChange({ cardBackPattern: id })} />
        ))}
      </Row>
      <Row title="Chip colour">
        {Object.entries(CHIP_COLOR_OPTIONS).map(([id, opt]) => (
          <Swatch
            key={`chip-${id}`}
            label={opt.label}
            selected={appearance.chipColor === id}
            onClick={() => onChange({ chipColor: id })}
            background={`radial-gradient(circle at 50% 40%, ${opt.a} 0%, ${opt.b} 80%), ${opt.border}`}
          />
        ))}
      </Row>
      <Row title="Chip style">
        {Object.entries(CHIP_STYLE_OPTIONS).map(([id, opt]) => (
          <OptionChip key={`chipstyle-${id}`} label={opt.label} active={appearance.chipStyle === id} onClick={() => onChange({ chipStyle: id })} />
        ))}
      </Row>
      <Row title="Table background">
        {Object.entries(BACKGROUND_OPTIONS).map(([id, opt]) => (
          <Swatch key={`bg-${id}`} label={opt.label} selected={appearance.background === id} onClick={() => onChange({ background: id })} background={opt.color} />
        ))}
      </Row>
      <Row title="Action timer glow">
        {Object.entries(GLOW_OPTIONS).map(([id, opt]) => (
          <Swatch
            key={`glow-${id}`}
            label={opt.label}
            selected={appearance.glow === id}
            onClick={() => onChange({ glow: id })}
            background={`radial-gradient(circle at 50% 50%, ${opt.color} 0%, rgba(0,0,0,0.85) 85%)`}
          />
        ))}
      </Row>
    </>
  );
}

function SolverSwatchRow({ title, value, onPick }) {
  return (
    <Row title={title}>
      {SOLVER_PALETTE.map((hex) => (
        <Box
          key={`${title}-${hex}`}
          onClick={() => onPick(hex)}
          title={hex}
          sx={{
            width: 26,
            height: 26,
            borderRadius: "7px",
            backgroundColor: hex,
            border: `2px solid ${value === hex ? "#ffffff" : "rgba(255,255,255,0.15)"}`,
            boxShadow: value === hex ? "0 0 0 2px rgba(86,236,207,0.45)" : "none",
            cursor: "pointer",
            transition: "all 140ms ease",
            "&:hover": { borderColor: "#ffffff" },
          }}
        />
      ))}
    </Row>
  );
}

function SolverSection({ prefs, onChange }) {
  return (
    <>
      <SolverSwatchRow title="Check / Call" value={prefs.checkCall} onPick={(hex) => onChange({ checkCall: hex })} />
      <SolverSwatchRow title="Fold" value={prefs.fold} onPick={(hex) => onChange({ fold: hex })} />
      <SolverSwatchRow title="Bet size 1" value={prefs.bet1} onPick={(hex) => onChange({ bet1: hex })} />
      <SolverSwatchRow title="Bet size 2" value={prefs.bet2} onPick={(hex) => onChange({ bet2: hex })} />
      <SolverSwatchRow title="Bet size 3" value={prefs.bet3} onPick={(hex) => onChange({ bet3: hex })} />
      <Row title="Deck colours">
        {Object.entries(SOLVER_DECK_OPTIONS).map(([id, opt]) => (
          <Button
            key={`deck-${id}`}
            size="small"
            onClick={() => onChange({ deck: id })}
            sx={{
              minWidth: "unset",
              px: 1,
              py: 0.4,
              borderRadius: "8px",
              border: `1px solid ${prefs.deck === id ? C.green : C.border}`,
              backgroundColor: prefs.deck === id ? "rgba(86,236,207,0.12)" : "rgba(255,255,255,0.02)",
              textTransform: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.7,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: prefs.deck === id ? C.green : C.textMuted }}>
              {opt.label}
            </Typography>
            <Box component="span" sx={{ display: "inline-flex", gap: 0.3 }}>
              {["s", "h", "d", "c"].map((suit) => (
                <Typography key={suit} component="span" sx={{ fontSize: 12, lineHeight: 1, color: opt.colors[suit] }}>
                  {SUIT_SYMBOLS[suit]}
                </Typography>
              ))}
            </Box>
          </Button>
        ))}
      </Row>
    </>
  );
}

export default function SettingsDialog({ open, onClose }) {
  const [section, setSection] = React.useState("solver");
  const [appearance, setAppearance] = React.useState(() => loadAppearance());
  const [solverPrefs, setSolverPrefs] = React.useState(() => loadSolverPrefs());

  React.useEffect(() => {
    if (open) {
      setAppearance(loadAppearance());
      setSolverPrefs(loadSolverPrefs());
    }
  }, [open]);

  const changeAppearance = (patch) => {
    setAppearance((prev) => {
      const next = { ...prev, ...patch };
      saveAppearance(next);
      return next;
    });
  };
  const changeSolver = (patch) => {
    setSolverPrefs((prev) => {
      const next = { ...prev, ...patch };
      saveSolverPrefs(next);
      return next;
    });
  };
  const resetCurrent = () => {
    if (section === "solver") changeSolver({ ...DEFAULT_SOLVER_PREFS });
    else changeAppearance({ ...DEFAULT_APPEARANCE });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#1f1f1f", border: `1px solid ${C.border}`, backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: C.white }}>Settings</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 1.6, pt: "6px !important" }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Colours and themes
        </Typography>
        <Box sx={{ display: "flex", gap: 0.6 }}>
          <OptionChip label="Solver" active={section === "solver"} onClick={() => setSection("solver")} />
          <OptionChip label="Trainer & Play" active={section === "trainer"} onClick={() => setSection("trainer")} />
        </Box>
        {section === "solver" ? (
          <SolverSection prefs={solverPrefs} onChange={changeSolver} />
        ) : (
          <TrainerSection appearance={appearance} onChange={changeAppearance} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetCurrent} sx={{ color: C.textMuted, textTransform: "none", fontSize: 12 }}>
          Reset to defaults
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ backgroundColor: C.green, color: "#111", fontWeight: 800, textTransform: "none", "&:hover": { backgroundColor: "#45b39b" } }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
